import React, { useState, useRef, RefObject } from 'react';
import md5 from 'md5';
import { Input } from '@fiverr-private/forms';
import { Button } from '@fiverr-private/button';
import { translate, I18n } from '@fiverr-private/i18n-react';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography, Text } from '@fiverr-private/typography';
import { logger } from '@fiverr-private/obs';
import { getContext } from '@fiverr-private/fiverr_context';
import { track as mixpanelTrack } from '../../../lib/mixpanel';
import { sendEvent as sendBQEvent } from '../../../lib/bigQuery';
import { sendReferrals } from '../../../lib/sendReferral';
import { useCampaign } from '../../hooks/useCampaign';
import { EmailPreview } from '../EmailPreview';
import { User } from '../../../lib/interfaces';

const COMMA_SEPARATION_EMAIL_REGEX = /^(?:\s*\S+\s*,\s*)*\s*\S+\s*(?:,)?$/;
const EMAIL_REGEX = /^[a-zA-Z0-9_.&%+\-']+@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,13}$/;

const NOTIFICATION_TYPES = {
  INFO: 'info',
  ALERT: 'alert',
};

const NOTIFICATION_MESSAGES = {
  EMAIL_INFO: 'referral_program_perseus.invitation.email.separate',
  EMAIL_REGEX_INVALID: 'referral_program_perseus.invitation.email.email_regex_invalid',
  COMMA_SEPARATION_REGEX_INVALID: 'referral_program_perseus.invitation.email.comma_separation_invalid',
  EMAIL_SENT: 'referral_program_perseus.invitation.email.email_sent',
  SEND_REFERRALS_FAILED: 'referral_program_perseus.invitation.email.send_referrals_failed',
};

interface EmailInviteProps {
  user?: User;
}

export const EmailInvite = ({ user }: EmailInviteProps) => {
  const emailRef = useRef<HTMLInputElement>(null) as RefObject<HTMLInputElement>;

  const [notification, setNotification] = useState({
    type: NOTIFICATION_TYPES.INFO,
    message: NOTIFICATION_MESSAGES.EMAIL_INFO,
  });
  const [loading, setLoading] = useState(false);

  const campaign = useCampaign();

  const onSendClick = () => {
    mixpanelTrack('Referral Page Inviter - Send Email Button - Click');

    const { userId } = getContext();
    const emails = getEmails().map((email) => md5(email));

    sendBQEvent({
      type: 'clicked_on_send_referral_program_email_invitation',
      referral_program: {
        referral_inviter: userId,
        referral_invitee: emails.join(','),
        num_referral_emails_sent: emails.length,
      },
      page: {
        element: {
          name: 'send_email_invitation',
          text: translate('referral_program_perseus.invitation.email.cta'),
          type: 'button',
        },
      },
      action: {
        type: 'click',
      },
    });
  };

  const showNotification = (message, type = 'info') => {
    setNotification({
      type,
      message,
    });

    setTimeout(resetNofication, 3000);
  };

  const resetNofication = () => {
    setNotification({
      type: NOTIFICATION_TYPES.INFO,
      message: NOTIFICATION_MESSAGES.EMAIL_INFO,
    });
  };

  const getEmails = () => {
    if (emailRef?.current) {
      return emailRef.current.value
        .split(',')
        .map((email) => email.trim())
        .map((email) => email.toLowerCase())
        .filter((email) => email);
    }
    return [];
  };

  const onSubmit = async (event) => {
    event.preventDefault();

    const isCommaSeparationValid = COMMA_SEPARATION_EMAIL_REGEX.test(emailRef?.current?.value || '');

    if (!isCommaSeparationValid) {
      showNotification(NOTIFICATION_MESSAGES.COMMA_SEPARATION_REGEX_INVALID, NOTIFICATION_TYPES.ALERT);

      return;
    }

    const emails = getEmails();

    if (!emails?.length) {
      return;
    }

    const emailsRegexValid = emails.every((email) => EMAIL_REGEX.test(email));

    if (!emailsRegexValid) {
      showNotification(NOTIFICATION_MESSAGES.EMAIL_REGEX_INVALID, NOTIFICATION_TYPES.ALERT);

      return;
    }

    try {
      setLoading(true);

      await sendReferrals(emails, campaign.slug);
      if (emailRef.current) {
        emailRef.current.value = '';
      }
      showNotification(NOTIFICATION_MESSAGES.EMAIL_SENT, NOTIFICATION_TYPES.INFO);
    } catch (error: any) {
      const { userGuid, userId, pageCtxId } = getContext();

      error.description = 'Failed on send referrals';

      logger.error(error, {
        userGuid,
        userId,
        pageCtxId,
      });

      showNotification(NOTIFICATION_MESSAGES.SEND_REFERRALS_FAILED, NOTIFICATION_TYPES.ALERT);
    } finally {
      setLoading(false);
    }
  };

  const isAlert = notification.type === NOTIFICATION_TYPES.ALERT;

  return (
    <Stack direction="column" gap="0">
      <form onSubmit={onSubmit} autoComplete="off">
        <label htmlFor="email">
          <Text color="bodyPrimary">
            <I18n k="referral_program_perseus.invitation.email.title" />
          </Text>
        </label>

        <Stack marginY="2" gap="2">
          <Input
            ref={emailRef}
            isError={isAlert}
            name="email"
            autoComplete="off"
            placeholder={translate('referral_program_perseus.invitation.email.placeholder')}
          />
          <Button type="submit" intent="primary" variant="filled" onClick={onSendClick} disabled={loading}>
            <I18n k="referral_program_perseus.invitation.email.cta" />
          </Button>
        </Stack>
      </form>
      <Stack justifyContent="spaceBetween" marginBottom="4" alignItems="flexStart">
        <Typography role="notification" size="b_sm" color={isAlert ? 'red_900' : 'grey_800'}>
          <I18n k={notification.message} />
        </Typography>
        <Container hidden={{ default: true, sm: false }} marginTop="4">
          <EmailPreview user={user} />
        </Container>
      </Stack>
    </Stack>
  );
};
